import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Gift } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const refCode = params.get("ref") || "";
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await signup(email, password, fullName, refCode || undefined);
      toast.success("Welcome to NewsLayer!");
      navigate("/");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md bg-[#121212] border border-white/10 rounded-xl p-8" data-testid="signup-form">
        <div className="flex justify-center mb-6"><Logo /></div>
        <h1 className="font-heading font-bold text-white text-xl text-center mb-1">Create your account</h1>
        <p className="text-gray-500 text-sm text-center mb-8">Save stories, follow topics, get the daily digest.</p>
        {refCode && (
          <div data-testid="referral-banner"
            className="flex items-start gap-2.5 bg-[#1a1505] border border-[#F59E0B]/30 rounded-lg p-3.5 mb-6">
            <Gift className="h-4 w-4 text-[#F59E0B] shrink-0 mt-0.5" />
            <p className="text-xs text-gray-300 leading-relaxed">
              You've been invited by a friend! Sign up and read <span className="text-white font-semibold">3 stories</span> to
              unlock a free month of Premium for them.
            </p>
          </div>
        )}
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="fullName" className="text-gray-300">Full name</Label>
            <Input id="fullName" required value={fullName} onChange={(e) => setFullName(e.target.value)}
              data-testid="signup-name-input" className="mt-1.5 bg-[#1A1A1A] border-white/10 text-white" />
          </div>
          <div>
            <Label htmlFor="email" className="text-gray-300">Email</Label>
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="signup-email-input" className="mt-1.5 bg-[#1A1A1A] border-white/10 text-white" />
          </div>
          <div>
            <Label htmlFor="password" className="text-gray-300">Password (min 8 characters)</Label>
            <Input id="password" type="password" required minLength={8} value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="signup-password-input" className="mt-1.5 bg-[#1A1A1A] border-white/10 text-white" />
          </div>
          {error && <p className="text-red-400 text-sm" data-testid="signup-error">{error}</p>}
          <Button type="submit" disabled={busy} data-testid="signup-submit-button"
            className="w-full bg-[#00E5FF] text-black font-bold hover:bg-[#00c9e0]">
            {busy ? "Creating account..." : "Create account"}
          </Button>
        </form>
        <p className="text-gray-500 text-sm text-center mt-6">
          Already registered?{" "}
          <Link to="/login" data-testid="signup-login-link" className="text-[#00E5FF] hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
